import { ElementRef } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as i0 from "@angular/core";
/**
 * Directive to make an element or component a valid drop target.
 *
 * By default, for efficiency, it does not propagate drag events.
 * To allow propagating events to ancestor elements, set `propagate` to true.
 *
 * To customize event handling, override methods in subclasses.
 */
export class DragDropDirective {
    get isValidDropTarget() {
        throw new Error('Not Implemented');
    }
    constructor(elemRef) {
        this.elemRef = elemRef;
        this.positiveHighlightClass = 'highlight-blue';
        this.negativeHighlightClass = 'bg-negative-5';
        this.propagate = false;
        this.isDragging = false;
        this.unsubscribe = new Subject();
        this.dragstartListener = null;
        this.dragendListener = null;
        this.isDraggable = new BehaviorSubject(false);
    }
    ngOnInit() {
        const elem = this.elemRef.nativeElement;
        // Adjust cursor and manage event listeners depending on whether the item can be dragged.
        this.isDraggable.pipe(takeUntil(this.unsubscribe)).subscribe((isDraggable_) => {
            if (isDraggable_) {
                elem.draggable = true;
                elem.style.cursor = 'grab';
                this.dragstartListener = this.dragstart.bind(this);
                this.dragendListener = this.dragend.bind(this);
                elem.addEventListener('dragstart', this.dragstartListener);
                elem.addEventListener('dragend', this.dragendListener);
            }
            else {
                elem.draggable = false;
                elem.style.cursor = 'default';
                // Remove unused listeners to improve performance
                if (this.dragstartListener)
                    elem.removeEventListener('dragstart', this.dragstartListener);
                if (this.dragendListener)
                    elem.removeEventListener('dragend', this.dragendListener);
            }
        });
        elem.addEventListener('dragenter', this.dragenter.bind(this));
        elem.addEventListener('dragover', this.dragover.bind(this));
        elem.addEventListener('dragleave', this.dragleave.bind(this));
        elem.addEventListener('drop', this.drop.bind(this));
    }
    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
    setIsDraggable(isDraggable_) {
        this.isDraggable.next(isDraggable_);
    }
    getDropTarget(_event) {
        return this.elemRef.nativeElement;
    }
    dragstart(event) {
        // Ignore events when this item is not draggable or when the event cannot carry any data.
        if (!this.isDraggable || !event.dataTransfer)
            return;
        if (!this.propagate) {
            event.stopPropagation();
        }
        this.isDragging = true;
        // Drag data must be set in the dragstart event; it cannot be set in other events
        this.setEventData(event);
    }
    setEventData(_event) {
        throw Error('Not Implemented');
    }
    dragend(event) {
        this.isDragging = false;
        const elem = this.getDropTarget(event);
        elem.classList.remove(this.positiveHighlightClass, this.negativeHighlightClass);
    }
    dragleave(event) {
        const elem = this.getDropTarget(event);
        elem.classList.remove(this.positiveHighlightClass, this.negativeHighlightClass);
    }
    dragenter(event) {
        const elem = this.getDropTarget(event);
        // Ignore events when this item is not a valid drop target or when the event does not carry any data.
        if (!this.isValidDropTarget || !event.dataTransfer) {
            if (!this.isDragging)
                elem.classList.add(this.negativeHighlightClass);
            return;
        }
        elem.classList.add(this.positiveHighlightClass);
        this.disableDefault(event);
    }
    dragover(event) {
        const elem = this.getDropTarget(event);
        // Ignore events when this item is not a valid drop target or when the event does not carry any data.
        if (!this.isValidDropTarget || !event.dataTransfer) {
            if (!this.isDragging)
                elem.classList.add(this.negativeHighlightClass);
            return;
        }
        elem.classList.add(this.positiveHighlightClass);
        this.disableDefault(event);
    }
    drop(event) {
        const elem = this.getDropTarget(event);
        elem.classList.remove(this.positiveHighlightClass, this.negativeHighlightClass);
        // Ignore events when this item is not a valid drop target or when the event does not carry any data.
        if (!this.isValidDropTarget || !event.dataTransfer)
            return;
        this.disableDefault(event);
        this.dropEventHandler(event);
    }
    dropEventHandler(_event) {
        throw Error('Not Implemented');
    }
    disableDefault(event) {
        // Disable default behavior to trigger drop event.
        // Note: Do not call this method in `dragStart`. It will inconsistently disable dragging functionality.
        // https://developer.mozilla.org/en-US/docs/Web/API/HTML_Drag_and_Drop_API/Drag_operations#specifying_drop_targets
        event.preventDefault();
        if (!this.propagate) {
            event.stopPropagation();
        }
    }
}
DragDropDirective.ɵfac = function DragDropDirective_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || DragDropDirective)(i0.ɵɵdirectiveInject(i0.ElementRef)); };
DragDropDirective.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: DragDropDirective, inputs: { positiveHighlightClass: "positiveHighlightClass", negativeHighlightClass: "negativeHighlightClass" } });
